/**
 * Display-only formatting helpers. They never mutate stored values.
 *
 * Two flavours of money display:
 *   • formatPrice / formatRupee   → drop ".00" for whole numbers (storefront, product tables)
 *   • formatRupeeFixed            → always 2 decimals (invoices, ledger, quotations, payments)
 *
 * Use formatRupeeOrDash for cells that should render "—" when the value is null/undefined.
 */

// Show up to 2 decimals with Indian comma grouping. Integer values render clean (no ".00").
// Examples: 2786.38 → "2,786.38", 100 → "100", 0.49 → "0.49"
export const formatPrice = (n) => {
  const num = Number(n || 0);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

// Always 2 decimals, plain number (no rupee sign) — for cells that prefix ₹ themselves.
// Examples: 100 → "100.00", 12345.6 → "12,345.60"
export const formatPriceFixed = (n) => {
  const num = Number(n || 0);
  if (!Number.isFinite(num)) return '0.00';
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Whole-rupee headline figure (rounds), no decimals.
// Examples: 12345.6 → "12,346", 100.2 → "100"
export const formatPriceInt = (n) => {
  const num = Number(n || 0);
  if (!Number.isFinite(num)) return '0';
  return Math.round(num).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

// Whole-rupee with the rupee sign prefix.
export const formatRupeeInt = (n) => `₹${formatPriceInt(n)}`;

// Same as formatPrice with the rupee sign prefix.
export const formatRupee = (n) => `\u20B9${formatPrice(n)}`;

// Always 2 decimals — for financial statements where "12,000.00" reads correctly.
// Examples: 100 → "₹100.00", 12345.6 → "₹12,345.60"
export const formatRupeeFixed = (n) => {
  const num = Number(n || 0);
  if (!Number.isFinite(num)) return '\u20B90.00';
  return `\u20B9${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Renders "—" when the value is null/undefined/NaN, otherwise formatRupee.
export const formatRupeeOrDash = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return formatRupee(n);
};

// Quantity display: drop ".0" but keep real decimals.
// Examples: 2.0 → "2", 2.5 → "2.5", 2 → "2", 2.50 → "2.5"
export const formatQty = (n) => {
  const num = Number(n || 0);
  if (!Number.isFinite(num)) return '0';
  if (Number.isInteger(num)) return String(num);
  // Trim trailing zeros after up to 3 decimals
  return num.toFixed(3).replace(/\.?0+$/, '');
};

// Quantity display that renders "—" when null.
export const formatQtyOrDash = (n) => (n == null ? '—' : formatQty(n));

// Percentage display: 1 decimal, drop trailing zero.
// Examples: 12.5 → "12.5%", 12.0 → "12%", 12 → "12%"
export const formatPercent = (n) => {
  const num = Number(n || 0);
  if (!Number.isFinite(num)) return '0%';
  if (Number.isInteger(num)) return `${num}%`;
  return `${num.toFixed(1).replace(/\.?0+$/, '')}%`;
};

// Round to 2 decimals as a Number (for calculations & <input value=> bindings).
// Prefer this over Math.round(x * 100) / 100 scattered across files.
export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
